'use client';

import { createContext, useContext, useState, useEffect } from 'react';
import { useSession, signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';

const AuthContext = createContext();

export function AuthContextProvider({ children }) {
  const { data: session, status, update } = useSession();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const router = useRouter();

  // Update isLoggedIn state when session changes
  useEffect(() => {
    setIsLoggedIn(status === 'authenticated' && !!session);

    // If session status changes to unauthenticated and was previously authenticated,
    // show the login prompt
    if (status === 'unauthenticated' && isLoggedIn) {
      setShowLoginPrompt(true);
    }
  }, [session, status, isLoggedIn]);

  // Function to handle login prompt
  const handleLoginPrompt = () => {
    setShowLoginPrompt(false);
    router.push('/login');
  };

  // Function to dismiss login prompt
  const dismissLoginPrompt = () => {
    setShowLoginPrompt(false);
  };

  // Login prompt component
  const LoginPrompt = () => {
    if (!showLoginPrompt) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold mb-4">Session Expired</h2>
          <p className="mb-6">Your session has expired. Please log in again to continue.</p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={dismissLoginPrompt}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Dismiss
            </button>
            <button
              onClick={handleLoginPrompt}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Log In
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AuthContext.Provider value={{
      isLoggedIn,
      user: session?.user,
      status,
      showLoginPrompt,
      setShowLoginPrompt
    }}>
      <LoginPrompt />
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
